/* ============================================================
   KidoBook product page - section-aware behaviour.
   Re-initialises on Shopify Theme Editor section load so it keeps
   working when sections are added / removed / re-rendered without
   a full page reload.
   ============================================================ */
(function () {
  'use strict';

  const productMobileQuery = window.matchMedia('(max-width: 768px)');

  /* ---------- Main product controller ---------- */
  class KidoBookProduct {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      const dataEl = root.querySelector('[data-kb-variant-data]');
      try {
        this.data = dataEl ? JSON.parse(dataEl.textContent) : { variants: [] };
      } catch (e) {
        this.data = { variants: [] };
      }
      this.variants = this.data.variants || [];
      this.optionSelectors = Array.from(root.querySelectorAll('[data-kb-option-index]'));
      this.forms = Array.from(root.querySelectorAll('[data-kb-product-form]'));
      this.init();
    }

    init() {
      this.bindOptions();
      this.bindGallery();
      this.bindQuantity();
      this.bindCartAction();
      this.bindStickySubmit();
      this.bindBundle();
      // Apply URL variant if present, else current selection.
      const params = new URLSearchParams(window.location.search);
      const urlVariant = params.get('variant');
      if (urlVariant && this.variants.some((v) => String(v.id) === String(urlVariant))) {
        this.selectVariantById(urlVariant);
      } else {
        this.updateFromSelection(false);
      }
    }

    bindOptions() {
      this.root.addEventListener('change', (e) => {
        if (e.target.matches('[data-kb-option-index] input, select[data-kb-option-index]')) {
          this.updateFromSelection(true);
        }
      });
    }

    selectedOptionValues() {
      const values = [];
      this.optionSelectors.forEach((el) => {
        const idx = parseInt(el.dataset.kbOptionIndex, 10);
        if (el.tagName === 'SELECT') {
          values[idx] = el.value;
        } else {
          const checked = el.querySelector('input:checked');
          if (checked) values[idx] = checked.value;
        }
      });
      return values;
    }

    findVariant(values) {
      return this.variants.find((v) =>
        v.options.every((opt, i) => values[i] == null || String(opt) === String(values[i]))
      );
    }

    selectVariantById(id) {
      const variant = this.variants.find((v) => String(v.id) === String(id));
      if (!variant) return;
      this.optionSelectors.forEach((el) => {
        const idx = parseInt(el.dataset.kbOptionIndex, 10);
        const val = variant.options[idx];
        if (el.tagName === 'SELECT') {
          el.value = val;
        } else {
          const input = el.querySelector('input[value="' + CSS.escape(String(val)) + '"]');
          if (input) input.checked = true;
        }
      });
      this.applyVariant(variant, false);
    }

    updateFromSelection(pushState) {
      const values = this.selectedOptionValues();
      const variant = this.findVariant(values) || null;
      this.applyVariant(variant, pushState);
    }

    applyVariant(variant, pushState) {
      // Update hidden id inputs on every product form in the section.
      this.forms.forEach((form) => {
        const idInput = form.querySelector('input[name="id"]');
        if (idInput) idInput.value = variant ? variant.id : '';
      });

      // Price displays.
      this.root.querySelectorAll('[data-kb-price]').forEach((wrap) => {
        const cur = wrap.querySelector('[data-kb-price-current]');
        const cmp = wrap.querySelector('[data-kb-price-compare]');
        const save = wrap.querySelector('[data-kb-price-save]');
        if (!variant) return;
        if (cur && !wrap.hasAttribute('data-kb-price-free')) cur.innerHTML = variant.price;
        if (cmp) {
          if (variant.compare_at) { cmp.innerHTML = variant.compare_at; cmp.hidden = false; }
          else { cmp.hidden = true; }
        }
        if (save) {
          if (variant.save) { save.innerHTML = save.dataset.kbSaveLabel ? save.dataset.kbSaveLabel.replace('[amount]', variant.save) : variant.save; save.hidden = false; }
          else { save.hidden = true; }
        }
      });

      // SKU.
      this.root.querySelectorAll('[data-kb-sku]').forEach((el) => {
        el.textContent = variant && variant.sku ? variant.sku : '';
      });

      // Availability -> buttons.
      const available = variant && variant.available;
      this.root.querySelectorAll('[data-kb-atc]').forEach((btn) => {
        const label = btn.querySelector('[data-kb-atc-label]') || btn;
        if (!variant) {
          btn.disabled = true;
          label.textContent = btn.dataset.kbUnavailable || 'Unavailable';
        } else if (!available) {
          btn.disabled = true;
          label.textContent = btn.dataset.kbSoldout || 'Sold out';
        } else {
          btn.disabled = false;
          label.textContent = btn.dataset.kbAdd || 'Add to cart';
        }
      });
      this.root.querySelectorAll('[data-kb-buynow]').forEach((btn) => {
        btn.disabled = !available;
      });

      // Stock message.
      this.root.querySelectorAll('[data-kb-stock]').forEach((el) => {
        if (!variant) { el.hidden = true; return; }
        el.hidden = false;
        el.classList.toggle('kb-stock--out', !available);
      });

      // Swap gallery media when the variant has its own image.
      if (variant && variant.media_id != null) {
        this.showMediaById(variant.media_id);
      }

      // Mark sold-out option labels (single-option products).
      this.markUnavailableOptions();

      // Sync the sticky mobile bar.
      this.syncSticky(variant);

      // URL / history.
      if (pushState && variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      this.currentVariant = variant;
    }

    markUnavailableOptions() {
      if (this.optionSelectors.length !== 1) return;
      const el = this.optionSelectors[0];
      if (el.tagName === 'SELECT') return;
      el.querySelectorAll('.kb-variant-option').forEach((opt) => {
        const input = opt.querySelector('input');
        const label = opt.querySelector('label');
        if (!input || !label) return;
        const match = this.variants.find((v) => String(v.options[0]) === String(input.value));
        label.classList.toggle('kb-soldout', !!match && !match.available);
      });
    }

    /* ----- Gallery ----- */
    bindGallery() {
      this.gallery = this.root.querySelector('[data-kb-gallery]');
      if (!this.gallery) return;
      this.gallery.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-kb-thumb]');
        if (thumb) {
          e.preventDefault();
          this.showSlide(thumb.dataset.kbThumb);
          return;
        }
        const stage = e.target.closest('[data-kb-stage].kb-zoomable');
        if (stage) stage.classList.toggle('is-zoomed');
      });
    }

    showSlide(mediaId) {
      if (!this.gallery) return;
      this.gallery.querySelectorAll('[data-kb-slide]').forEach((s) => {
        s.classList.toggle('is-active', s.dataset.kbSlide === String(mediaId));
      });
      this.gallery.querySelectorAll('[data-kb-thumb]').forEach((t) => {
        t.classList.toggle('is-active', t.dataset.kbThumb === String(mediaId));
      });
      const stage = this.gallery.querySelector('[data-kb-stage]');
      if (stage) stage.classList.remove('is-zoomed');
    }

    showMediaById(mediaId) {
      if (!this.gallery) return;
      const slide = this.gallery.querySelector('[data-kb-slide="' + CSS.escape(String(mediaId)) + '"]');
      if (slide) this.showSlide(mediaId);
    }

    /* ----- Quantity ----- */
    bindQuantity() {
      this.root.querySelectorAll('[data-kb-qty]').forEach((wrap) => {
        const input = wrap.querySelector('input');
        wrap.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-kb-qty-step]');
          if (!btn || !input) return;
          const step = parseInt(btn.dataset.kbQtyStep, 10);
          const min = parseInt(input.min, 10) || 1;
          let val = (parseInt(input.value, 10) || min) + step;
          if (val < min) val = min;
          if (input.max && val > parseInt(input.max, 10)) val = parseInt(input.max, 10);
          input.value = val;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }

    /* ----- Cart submit behaviour ----- */
    bindCartAction() {
      const behavior = this.root.dataset.kbCartAction || 'page';
      if (behavior === 'page') return; // native submit - most compatible.
      this.forms.forEach((form) => {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const btn = form.querySelector('[data-kb-atc]');
          if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
          })
            .then((r) => r.json())
            .then(() => {
              document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
              document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
              if (behavior === 'drawer') {
                const opener = document.querySelector('[data-kb-cart-open], a[href$="/cart"]');
                // Try known cart drawer togglers, else go to cart.
                const drawer = document.querySelector('cart-drawer-component, cart-drawer, #cart-drawer');
                if (drawer && typeof drawer.open === 'function') drawer.open();
                else window.location.href = window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart' : '/cart';
              } else {
                window.location.href = window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart' : '/cart';
              }
            })
            .catch(() => { form.submit(); });
        });
      });
    }

    /* ----- Sticky bar button submits the main product form ----- */
    bindStickySubmit() {
      const bar = document.querySelector('[data-kb-sticky-atc][data-for-section="' + this.sectionId + '"]');
      if (!bar) return;
      const btn = bar.querySelector('[data-kb-sticky-submit]');
      if (!btn || !this.forms.length) return;
      btn.addEventListener('click', () => {
        const form = this.forms[0];
        if (form.requestSubmit) form.requestSubmit();
        else form.submit();
      });
    }

    /* ----- "Add all to cart" for the bought-together card ----- */
    bindBundle() {
      this.root.querySelectorAll('[data-kb-add-all]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const ids = (btn.dataset.kbAddAll || '').split(',').map((s) => s.trim()).filter(Boolean);
          if (!ids.length) return;
          const items = ids.map((id) => ({ id: Number(id), quantity: 1 }));
          btn.disabled = true;
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items }),
          })
            .then((r) => r.json())
            .then(() => { window.location.href = (window.Shopify?.routes?.root || '/') + 'cart'; })
            .catch(() => { btn.disabled = false; });
        });
      });
    }

    /* ----- Sticky mobile add-to-cart bar ----- */
    syncSticky(variant) {
      const bar = document.querySelector('[data-kb-sticky-atc][data-for-section="' + this.sectionId + '"]');
      if (!bar) return;
      const cur = bar.querySelector('[data-kb-price-current]');
      if (cur && variant) cur.innerHTML = bar.hasAttribute('data-kb-price-free') ? cur.innerHTML : variant.price;
      const cmp = bar.querySelector('[data-kb-price-compare]');
      if (cmp) { if (variant && variant.compare_at) { cmp.innerHTML = variant.compare_at; cmp.hidden = false; } else { cmp.hidden = true; } }
    }

    destroy() { /* listeners are on root; GC on section unload */ }
  }

  /* ---------- Tabs (smooth-scroll to detail panels) ---------- */
  function bindTabs(root) {
    const tabs = root.querySelectorAll('[data-kb-tab]');
    if (!tabs.length) return;
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = root.querySelector('#' + tab.dataset.kbTab);
        tabs.forEach((t) => { t.classList.toggle('is-active', t === tab); t.setAttribute('aria-selected', t === tab); });
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ---------- Accordions using <button> + aria (progressive) ---------- */
  function bindAccordions(root) {
    root.querySelectorAll('[data-kb-acc-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const body = btn.nextElementSibling;
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        if (body) body.hidden = open;
      });
    });
  }

  /* ---------- Recently viewed (localStorage, cache-safe) ---------- */
  function recordRecentlyViewed() {
    const meta = document.querySelector('[data-kb-current-product]');
    if (!meta) return;
    let item;
    try { item = JSON.parse(meta.textContent); } catch (e) { return; }
    if (!item || !item.url) return;
    let list = [];
    try { list = JSON.parse(localStorage.getItem('kb_recently_viewed') || '[]'); } catch (e) { list = []; }
    list = list.filter((p) => p.url !== item.url);
    list.unshift(item);
    list = list.slice(0, 12);
    try { localStorage.setItem('kb_recently_viewed', JSON.stringify(list)); } catch (e) { /* private mode */ }
  }

  function renderRecentlyViewed(root) {
    const grid = root.querySelector('[data-kb-recent-grid]');
    if (!grid) return;
    let list = [];
    try { list = JSON.parse(localStorage.getItem('kb_recently_viewed') || '[]'); } catch (e) { list = []; }
    const currentUrl = grid.dataset.kbCurrentUrl || '';
    const max = parseInt(grid.dataset.kbMax, 10) || 6;
    const items = list.filter((p) => p.url !== currentUrl).slice(0, max);
    const sec = grid.closest('[data-kb-recent-section]');
    if (!items.length) { if (sec) sec.hidden = true; return; }
    if (sec) sec.hidden = false;
    grid.innerHTML = items.map((p) => (
      '<a class="kb-recent-card" href="' + escapeAttr(p.url) + '">' +
      (p.image ? '<img class="kb-recent-card__thumb" src="' + escapeAttr(p.image) + '" alt="" loading="lazy" width="38" height="51">' : '<span class="kb-recent-card__thumb"></span>') +
      '<span class="kb-recent-card__info">' +
      '<span class="kb-recent-card__title">' + escapeHtml(p.title) + '</span>' +
      '<span class="kb-recent-card__price">' + (p.price || '') + '</span>' +
      '</span></a>'
    )).join('');
  }

  function escapeHtml(s) { const d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; }
  function escapeAttr(s) { return escapeHtml(s).replace(/"/g, '&quot;'); }

  /* ---------- Mobile product flow ---------- */
  function syncMobileProductLayout(root) {
    if (!root || !root.matches('[data-kb-main]')) return;

    const buy = root.querySelector('[data-kb-buy-block]');
    const mobileAnchor = root.querySelector('[data-kb-mobile-buy-anchor]');
    if (!buy || !mobileAnchor) return;

    // Remember the merchant-selected desktop position before moving the same
    // live Shopify product form. Moving the node keeps dynamic checkout intact.
    if (!buy.__kbDesktopParent) {
      buy.__kbDesktopParent = buy.parentNode;
      buy.__kbDesktopNext = buy.nextSibling;
    }

    const moveBelowFormat = productMobileQuery.matches && root.dataset.kbMobileBuyAfterFormat === 'true';
    if (moveBelowFormat) {
      if (mobileAnchor.nextElementSibling !== buy) {
        mobileAnchor.insertAdjacentElement('afterend', buy);
      }
      buy.classList.add('is-mobile-position');
      return;
    }

    const desktopParent = buy.__kbDesktopParent;
    const desktopNext = buy.__kbDesktopNext;
    if (desktopParent && buy.parentNode !== desktopParent) {
      if (desktopNext && desktopNext.parentNode === desktopParent) {
        desktopParent.insertBefore(buy, desktopNext);
      } else {
        desktopParent.appendChild(buy);
      }
    }
    buy.classList.remove('is-mobile-position');
  }

  function syncAllMobileProductLayouts() {
    const roots = Array.from(document.querySelectorAll('[data-kb-main]'));
    roots.forEach(syncMobileProductLayout);
    const hideMobileHeader = productMobileQuery.matches && roots.some((root) => root.dataset.kbHideMobileHeader === 'true');
    document.body.classList.toggle('kb-product-mobile-header-hidden', hideMobileHeader);
  }

  /* ---------- Sticky mobile bar visibility ---------- */
  function bindStickyVisibility() {
    const bar = document.querySelector('[data-kb-sticky-atc]');
    if (!bar || bar.dataset.kbStickyBound) return;
    bar.dataset.kbStickyBound = '1';
    const anchor = document.querySelector('[data-kb-buy-block]') || document.querySelector('[data-kb-buy-anchor]');
    if (!anchor || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    io.observe(anchor);
  }

  /* ---------- Bootstrapping ---------- */
  function initSection(root) {
    if (!root) return;
    // Idempotency guard: the asset is included by every KidoBook section, so
    // the IIFE can run several times. Bind each root only once. A section that
    // the Theme Editor re-renders arrives as fresh DOM without the flag, so it
    // re-initialises correctly.
    if (root.dataset.kbInited) return;
    root.dataset.kbInited = '1';
    if (root.matches('[data-kb-main]')) {
      new KidoBookProduct(root);
      syncMobileProductLayout(root);
    }
    bindTabs(root);
    bindAccordions(root);
    renderRecentlyViewed(root);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-kb-main], [data-kb-tabs], [data-kb-recent-section]').forEach(initSection);
  }

  // Bootstrap exactly once per page, even though the asset is included by
  // every KidoBook section (so this IIFE may execute several times).
  if (!window.__kbProductBooted) {
    window.__kbProductBooted = true;

    const boot = function () {
      recordRecentlyViewed();
      initAll(document);
      syncAllMobileProductLayouts();
      bindStickyVisibility();
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }

    // Theme Editor: re-init a section when it is (re)loaded.
    document.addEventListener('shopify:section:load', function (e) {
      initAll(e.target);
      syncAllMobileProductLayouts();
      bindStickyVisibility();
    });

    document.addEventListener('shopify:section:unload', function () {
      window.requestAnimationFrame(syncAllMobileProductLayouts);
    });

    if (productMobileQuery.addEventListener) {
      productMobileQuery.addEventListener('change', syncAllMobileProductLayouts);
    } else if (productMobileQuery.addListener) {
      productMobileQuery.addListener(syncAllMobileProductLayouts);
    }
  }
})();
